/**
 * ProjectsPage Component
 * Displays a grid of projects with filtering and search
 * Projects are automatically filtered by the current pane
 */

import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Grid3X3, List, Filter, RefreshCw, FolderGit2, GitBranch, LayoutGrid, ExternalLink } from 'lucide-react';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { projectsApi, type Project, type CreateProjectRequest, type ProjectStatus, type ProjectType } from '../api/projects';
import { CreateProjectModal, ProjectsEmptyState } from '../components/projects';
import { PageLayout } from '../components/layout/PageLayout';
import { useToast } from '../contexts/ToastContext';
import { useProject, getProjectShortId } from '../contexts/ProjectContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usePane } from '../shared/contexts/PaneContext';
import { DataTable, DataTableToolbar, useDataTablePrefs } from '../components/shared/DataTable';

type ViewMode = 'grid' | 'list';
type FilterTab = 'all' | 'active' | 'setup' | 'archived';

const filterTabs: { value: FilterTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'setup', label: 'Setting Up' },
  { value: 'archived', label: 'Archived' },
];

const statusConfig: Record<ProjectStatus, { bg: string; text: string; dot: string; label: string }> = {
  setup: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Setting up' },
  active: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Active' },
  paused: { bg: 'bg-neutral-100', text: 'text-neutral-600', dot: 'bg-neutral-400', label: 'Paused' },
  archived: { bg: 'bg-neutral-100', text: 'text-neutral-500', dot: 'bg-neutral-400', label: 'Archived' },
};

const typeLabels: Record<ProjectType, string> = {
  service: 'Service',
  app: 'Application',
  library: 'Library',
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// =============================================================================
// Column config for list view
// =============================================================================

const ALL_COLUMNS = [
  { id: 'name', label: 'Project' },
  { id: 'type', label: 'Type' },
  { id: 'branch', label: 'Branch' },
  { id: 'updated', label: 'Updated' },
  { id: 'status', label: 'Status' },
];

const DEFAULT_VISIBLE = ['name', 'type', 'branch', 'updated', 'status'];

export function ProjectsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { projects, allProjects, isLoading: loading, error, refreshProjects } = useProject();
  const { currentWorkspace, wsSlug } = useWorkspace();
  const { paneInfo } = usePane();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { prefs, toggleColumn, setDensity, setSorting } = useDataTablePrefs('projects', DEFAULT_VISIBLE);

  // Refresh projects handler
  const fetchProjects = async () => {
    setIsRefreshing(true);
    try {
      await refreshProjects();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter and search projects
  const filteredProjects = useMemo(() => {
    let result = projects;

    // Apply status filter
    if (activeFilter !== 'all') {
      const statusMap: Record<FilterTab, ProjectStatus[]> = {
        all: [],
        active: ['active'],
        setup: ['setup'],
        archived: ['archived', 'paused'],
      };
      const statuses = statusMap[activeFilter];
      if (statuses.length > 0) {
        result = result.filter((p) => statuses.includes(p.status));
      }
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.slug.toLowerCase().includes(query) ||
          p.repository.url.toLowerCase().includes(query)
      );
    }

    return result;
  }, [projects, activeFilter, searchQuery]);

  // Create project handler
  const handleCreateProject = async (data: CreateProjectRequest) => {
    try {
      const newProject = await projectsApi.create(data);
      await refreshProjects();
      toast.success(`Project "${newProject.name}" created successfully.`);
    } catch (err) {
      console.error('Failed to create project:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create project');
      throw err;
    }
  };

  // Count by status
  const statusCounts = useMemo(() => {
    return projects.reduce(
      (acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [projects]);

  // Column definitions for list view
  const columns = useMemo<ColumnDef<Project, any>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Project',
      cell: ({ row }) => {
        const project = row.original;
        const shortId = getProjectShortId(project.id);
        return (
          <Link to={wsSlug ? `/w/${wsSlug}/product/${shortId}` : `/product/${shortId}`} className="flex items-center gap-3 min-w-0" onClick={e => e.stopPropagation()}>
            <div className="w-8 h-8 rounded-lg bg-neutral-100 group-hover:bg-coral-50
                          flex items-center justify-center text-neutral-400 group-hover:text-coral-500
                          transition-colors shrink-0">
              <FolderGit2 size={16} />
            </div>
            <div className="min-w-0">
              <span className="font-medium text-sm text-neutral-900 group-hover:text-coral-600 truncate block transition-colors">
                {project.name}
              </span>
              <span className="text-[11px] text-neutral-400 font-mono">{project.slug}</span>
            </div>
          </Link>
        );
      },
      enableSorting: true,
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: 'Type',
      cell: ({ getValue }) => (
        <span className="text-xs text-neutral-500">{typeLabels[getValue() as ProjectType]}</span>
      ),
      enableSorting: true,
    },
    {
      id: 'branch',
      accessorFn: row => row.repository.branch,
      header: 'Branch',
      cell: ({ getValue }) => (
        <span className="font-mono text-[11px] text-neutral-500">{getValue() as string}</span>
      ),
      enableSorting: true,
    },
    {
      id: 'updated',
      accessorKey: 'updatedAt',
      header: 'Updated',
      cell: ({ getValue }) => (
        <span className="text-xs text-neutral-400">{formatRelativeTime(getValue() as string)}</span>
      ),
      enableSorting: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue() as ProjectStatus;
        const cfg = statusConfig[s];
        return (
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${cfg.bg} ${cfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </div>
        );
      },
      enableSorting: true,
    },
  ], []);

  const columnVisibility = useMemo<VisibilityState>(() => {
    const vis: VisibilityState = {};
    ALL_COLUMNS.forEach(c => { vis[c.id] = prefs.visibleColumns.includes(c.id); });
    return vis;
  }, [prefs.visibleColumns]);

  return (
    <PageLayout variant="constrained" contentBackground="#FAFAF8">
      <div className="py-10 space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-coral-500 mb-2">
              {paneInfo.name}
            </p>
            <h1 className="text-4xl font-bold text-neutral-900 tracking-tight">Projects</h1>
            <p className="mt-2 text-neutral-500">
              {projects.length} project{projects.length !== 1 ? 's' : ''} in this workspace
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-900 hover:bg-neutral-800 text-white rounded-lg
                     text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Plus size={16} strokeWidth={2.5} />
            New Project
          </button>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-4 gap-px bg-neutral-200 rounded-xl overflow-hidden">
          {[
            { label: 'Total', value: projects.length, color: 'text-neutral-900' },
            { label: 'Active', value: statusCounts['active'] || 0, color: 'text-emerald-600' },
            { label: 'Setting Up', value: statusCounts['setup'] || 0, color: 'text-amber-600' },
            { label: 'Archived', value: (statusCounts['archived'] || 0) + (statusCounts['paused'] || 0), color: 'text-neutral-500' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white px-5 py-4">
              <div className={`text-2xl font-bold tabular-nums ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-neutral-500 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Filter Tabs */}
          <div className="flex items-center gap-1 p-1 bg-white border border-neutral-200 rounded-lg">
            {filterTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveFilter(tab.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-150 ${
                  activeFilter === tab.value
                    ? 'bg-neutral-900 text-white shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 relative min-w-[200px]">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm
                       focus:outline-none focus:ring-1 focus:ring-neutral-900/10 focus:border-neutral-400
                       placeholder:text-neutral-400 transition-all duration-150"
            />
          </div>

          {/* View Toggle & Refresh */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={fetchProjects}
              disabled={loading || isRefreshing}
              className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-white border border-transparent
                       hover:border-neutral-200 rounded-lg transition-all duration-150"
              title="Refresh"
            >
              <RefreshCw size={15} className={(loading || isRefreshing) ? 'animate-spin' : ''} />
            </button>
            <div className="flex items-center p-0.5 bg-white border border-neutral-200 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-all duration-150 ${
                  viewMode === 'grid'
                    ? 'bg-neutral-900 text-white shadow-sm'
                    : 'text-neutral-400 hover:text-neutral-700'
                }`}
                title="Grid view"
              >
                <Grid3X3 size={14} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-all duration-150 ${
                  viewMode === 'list'
                    ? 'bg-neutral-900 text-white shadow-sm'
                    : 'text-neutral-400 hover:text-neutral-700'
                }`}
                title="List view"
              >
                <List size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* List view toolbar - only show when in list mode */}
        {viewMode === 'list' && !loading && !error && filteredProjects.length > 0 && (
          <DataTableToolbar
            allColumns={ALL_COLUMNS}
            visibleColumns={prefs.visibleColumns}
            onToggleColumn={toggleColumn}
            density={prefs.density}
            onDensityChange={setDensity}
          />
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
              <span className="text-sm text-neutral-500">Loading projects...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <p className="text-red-700 font-medium mb-1">Failed to load projects</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchProjects}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200
                       text-red-700 rounded-lg text-sm font-medium transition-colors"
            >
              <RefreshCw size={14} />
              Try Again
            </button>
          </div>
        ) : filteredProjects.length === 0 ? (
          projects.length === 0 ? (
            <ProjectsEmptyState onCreateClick={() => setIsCreateModalOpen(true)} />
          ) : (
            <div className="bg-white border border-neutral-200 rounded-xl p-16 text-center">
              <Filter size={28} className="mx-auto text-neutral-300 mb-3" />
              <p className="text-neutral-600 font-medium">No projects match your filters</p>
              <p className="text-neutral-400 text-sm mt-1">
                Try adjusting your search or filter criteria
              </p>
              <button
                onClick={() => { setSearchQuery(''); setActiveFilter('all'); }}
                className="mt-4 text-neutral-900 hover:text-coral-600 font-medium text-sm underline underline-offset-2"
              >
                Clear filters
              </button>
            </div>
          )
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <ProjectGridCard key={project.id} project={project} />
            ))}
          </div>
        ) : (
          <DataTable<Project>
            data={filteredProjects}
            columns={columns}
            sorting={prefs.sorting}
            onSortingChange={setSorting}
            columnVisibility={columnVisibility}
            density={prefs.density}
            getRowId={row => row.id}
            onRowClick={row => navigate(wsSlug ? `/w/${wsSlug}/pr/${getProjectShortId(row.id)}` : `/pr/${getProjectShortId(row.id)}`)}
          />
        )}
      </div>

      {/* Create Modal */}
      {currentWorkspace?.id && (
        <CreateProjectModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateProject}
          workspaceId={currentWorkspace.id}
        />
      )}
    </PageLayout>
  );
}

/** Grid card with project-scoped navigation */
function ProjectGridCard({ project }: { project: Project }) {
  const shortId = getProjectShortId(project.id);
  const status = statusConfig[project.status];
  const repoName = project.repository.url.split('/').pop()?.replace('.git', '') || project.repository.url;
  const boardCount = project.boards?.length || 0;

  return (
    <Link
      to={`/product/${shortId}`}
      /* Note: legacy URL — WorkspaceRouter's LegacyPaneRedirect catches /pr/ and adds /w/:ws prefix */
      className="group block bg-white rounded-xl border border-neutral-200/80 p-5
               hover:shadow-lg hover:shadow-neutral-200/50 hover:border-neutral-300
               transition-all duration-200 ease-out"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neutral-100 to-neutral-50
                        flex items-center justify-center text-neutral-500
                        group-hover:from-coral-100 group-hover:to-coral-50 group-hover:text-coral-600
                        transition-all duration-200">
            <FolderGit2 size={20} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-neutral-900 group-hover:text-coral-600 truncate transition-colors">
              {project.name}
            </h3>
            <p className="text-[11px] text-neutral-400 font-mono">{project.slug}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium shrink-0 ${status.bg} ${status.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot} ${project.status === 'active' ? 'animate-pulse' : ''}`} />
          {status.label}
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-neutral-100 text-neutral-600 text-[11px]">
          {typeLabels[project.type]}
        </span>
        {boardCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-neutral-50 text-neutral-500 text-[11px]">
            <LayoutGrid size={10} />
            {boardCount} board{boardCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Repo */}
      <div className="space-y-1.5 pt-3 border-t border-neutral-100">
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <FolderGit2 size={12} className="text-neutral-400 shrink-0" />
          <span className="truncate">{repoName}</span>
          {project.repository.url.startsWith('http') && (
            <ExternalLink size={10} className="text-neutral-300 shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <GitBranch size={12} className="text-neutral-400 shrink-0" />
          <span className="font-mono text-[11px]">{project.repository.branch}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center justify-between">
        <span className="text-[11px] text-neutral-400">Updated {formatRelativeTime(project.updatedAt)}</span>
        <span className="text-[11px] font-medium text-coral-500 opacity-0 group-hover:opacity-100 transition-opacity">
          Open
        </span>
      </div>
    </Link>
  );
}

export default ProjectsPage;
